import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogContent,
  IconButton, InputAdornment, Stack, TextField, Typography,
} from "@mui/material";
import { ArrowBackIosNew, Close, DeleteOutline, Visibility, VisibilityOff } from "@mui/icons-material";
import { GoogleLogin, googleLogout } from "@react-oauth/google";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../hooks/redux-hooks";
import { logout } from "../slices/authSlice";
import {
  AccountDeletionInfo, AccountDeletionProof, deleteMyAccount, getAccountDeletionInfo,
} from "../api/accountDeletion";
import { getLocalizedErrorMessage, handleTokenExpiration } from "../utils/errorUtils";

export const accountDialogPaperSx = {
  borderRadius: 3, width: "100%", m: 2, maxWidth: 480,
};

const AccountDeletionIcon = () => (
  <Box sx={{
    bgcolor: "#fdebed", color: "#d71920",
    borderRadius: "50%", width: 88, height: 88,
    display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 3,
  }}>
    <DeleteOutline sx={{ fontSize: 48 }} />
  </Box>
);

const DeleteAccountDialog = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [step, setStep] = useState<"confirm" | "verify">("confirm");
  const [info, setInfo] = useState<AccountDeletionInfo | null>(null);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const sessionToken = useRef("");

  const reportError = (failure: any) => {
    setError(getLocalizedErrorMessage(failure, t("accountDeleteFailed")));
    handleTokenExpiration(failure?.code);
  };

  useEffect(() => {
    if (!open) return;
    let active = true;
    // Keep the same identity throughout the dialog, even if another tab logs in.
    sessionToken.current = localStorage.getItem("token") || "";
    setStep("confirm");
    setPassword("");
    setShowPassword(false);
    setError("");
    setInfo(null);
    setLoading(true);
    getAccountDeletionInfo(sessionToken.current).then((result) => {
      if (active) setInfo(result);
    }).catch((failure) => {
      if (active) {
        setError(getLocalizedErrorMessage(failure, t("accountDeleteFailed")));
        handleTokenExpiration(failure?.code);
      }
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, t]);

  const submit = async (proof: AccountDeletionProof) => {
    if (submittingRef.current) return;
    if (info?.loginType !== 2 && !proof.password) {
      setError(t("pleaseInputPassword"));
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    setError("");
    try {
      await deleteMyAccount(proof, sessionToken.current);
      // Suppress automatic Google sign-in after the local account is deleted.
      try { googleLogout(); } catch { /* Session cleanup must not depend on the Google SDK. */ }
      localStorage.removeItem("savedOrder");
      // Leave the protected route before clearing auth, so its guard cannot redirect to login.
      flushSync(() => navigate("/account-deleted", { replace: true }));
      await dispatch(logout()).unwrap();
    } catch (failure) {
      reportError(failure);
      // Refresh the nonce and login mode in case the popup expired or another tab switched modes.
      try { setInfo(await getAccountDeletionInfo(sessionToken.current)); } catch { /* Keep the original error. */ }
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => { if (!submitting) onClose(); }}
      aria-labelledby="delete-account-title" aria-describedby="delete-account-description"
      PaperProps={{ sx: accountDialogPaperSx }}>
      <Box sx={{ display: "flex", justifyContent: step === "verify" ? "space-between" : "flex-end", p: 1 }}>
        {step === "verify" && <Button disabled={submitting} startIcon={<ArrowBackIosNew />}
          onClick={() => { setStep("confirm"); setPassword(""); setError(""); }}>
          {t("btnBack")}
        </Button>}
        <IconButton aria-label={t("accountDeleteClose")} disabled={submitting} onClick={onClose}><Close /></IconButton>
      </Box>
      <DialogContent sx={{ px: { xs: 3, sm: 4 }, pb: 4, pt: 0, textAlign: "center" }}>
        <AccountDeletionIcon />
        <Typography id="delete-account-title" component="h2" variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
          {t(step === "confirm" ? "accountDeleteTitle" : "accountDeleteVerifyTitle")}
        </Typography>
        <Typography id="delete-account-description" sx={{ mb: 3, lineHeight: 1.65 }}>
          {t(step === "confirm" ? "accountDeleteDescription" :
            info?.loginType === 2 ? "accountDeleteGoogleDescription" : "accountDeletePasswordDescription")}
        </Typography>
        {step === "verify" && <Alert severity="warning" icon={false} sx={{ mb: 2, textAlign: "left", bgcolor: "#fdebed", color: "#bb1720" }}>
          {t("accountDeleteWarning")}
        </Alert>}
        {error && <Alert severity="error" sx={{ mb: 2, textAlign: "left" }}>{error}</Alert>}
        <Stack spacing={2}>
          {step === "confirm" ? (
            <Button variant="contained" color="error" size="large" disabled={loading || submitting}
              onClick={async () => {
                if (info) { setStep("verify"); setError(""); return; }
                setLoading(true);
                try { setInfo(await getAccountDeletionInfo(sessionToken.current)); setError(""); setStep("verify"); }
                catch (failure) { reportError(failure); }
                finally { setLoading(false); }
              }}>
              {loading ? <CircularProgress size={24} color="inherit" /> : t("accountDeleteContinue")}
            </Button>
          ) : info?.loginType === 2 ? (
            <Box sx={{ display: "flex", justifyContent: "center", pointerEvents: submitting ? "none" : "auto", opacity: submitting ? 0.6 : 1 }}>
              <GoogleLogin key={info.nonce + i18n.language} nonce={info.nonce}
                locale={i18n.language === "zh" ? "zh-TW" : "en"} text="continue_with" theme="outline"
                onSuccess={(result) => {
                  if (!result.credential) { setError(t("errCode11020")); return; }
                  void submit({ credential: result.credential, nonce: info.nonce });
                }} onError={() => setError(t("errCode11020"))} />
            </Box>
          ) : (
            <Box component="form" onSubmit={(event) => { event.preventDefault(); void submit({ password }); }}>
              <TextField fullWidth autoFocus type={showPassword ? "text" : "password"}
                label={t("password")} value={password} autoComplete="current-password" disabled={submitting}
                onChange={(event) => setPassword(event.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">
                  <IconButton aria-label={t(showPassword ? "accountDeleteHidePassword" : "accountDeleteShowPassword")}
                    onClick={() => setShowPassword(!showPassword)} edge="end" disabled={submitting}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment> }} />
              <Button type="submit" fullWidth variant="contained" color="error" size="large" disabled={submitting} sx={{ mt: 2 }}>
                {t(submitting ? "accountDeleteDeleting" : "accountDeleteButton")}
              </Button>
            </Box>
          )}
          {submitting && info?.loginType === 2 && <Typography role="status">{t("accountDeleteDeleting")}</Typography>}
          <Button variant="outlined" color="inherit" size="large" disabled={submitting} onClick={onClose}>{t("btnCancel")}</Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteAccountDialog;
